import { Component, inject, input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    CompletedPaymentOnPay,
    PaymentMethod,
    PaymentMethods,
    PaymentMethodsType,
    PaymentResponse,
    SaleStatus,
} from '@cargos/sprintpay-models';
import { PaymentsService } from '@cargos/sprintpay-services';
import { Banner } from '@cargos/sprintpay-ui';
import { Observable, of, take } from 'rxjs';
import { ConfigService, CurrentFacilityService, PaymentConfirmationService } from 'src/app/shared/services';

@Component({
    selector: 'app-sprintpay-confirmation',
    templateUrl: './confirmation.component.html',
    styleUrls: ['./confirmation.component.scss'],
    standalone: false,
})
export class ConfirmationComponent implements OnInit {
    public isDownloading = false;
    public successfulResponse: PaymentResponse[] = [];
    public completedPayments: CompletedPaymentOnPay[] = [];
    public errorResponse: PaymentResponse[] = [];
    public facilityName: string;
    public notificationEmail: string | null;
    public records: number[] = [];
    public paymentResponse: PaymentResponse[] = [];
    public countPaymentErrors: number = 0;
    public countPaymentSuccessful: number = 0;
    public totalPayments: number = 0;
    public isCreditPaymentMethod: boolean = false;
    public isTherePaymentMethodDifferentCredit: boolean = false;
    public uniquePayments: string[] = [];
    public differentPaymentMethods?: boolean;
    public paymentReceiptText: string = 'Download Receipt';
    public readonly paymentMethods = PaymentMethods;
    public banner: Banner;
    public transactionsIds: string[] = [];
    public disabledButton = input<boolean>(false);
    public showReceiptSent = input<boolean>(true);
    private configurationService = inject(ConfigService);

    constructor(
        private paymentsService: PaymentsService,
        private router: Router,
        private paymentConfirmationService: PaymentConfirmationService,
        private currentFacilityService: CurrentFacilityService
    ) {}

    ngOnInit(): void {
        this.subscribeConfirmationInformation();
        this.facilityName = this.currentFacilityService.getFacilitySource();
        this.initBanner();
    }

    initBanner(): void {
        this.banner = {
            id: 'error_banner',
            message: `<div>
                        <p>
                            <span class="mat-subtitle-1 banner-title"
                                >Unable to complete ${this.countPaymentErrors} of your
                                transactions</span
                            >
                        </p>
                        <p class="mat-subtitle-1 banner-description">
                            We could not process all of the transactions in your cart due
                            to a technical issue on our end. Please go to your cart and
                            try again. If the issue keeps happening, contact
                            <a href="mailto: support@cargosprint.com">
                                support&#64;cargosprint.com
                            </a>
                        </p>
                    </div>`,
            icon: 'warning',
            display: true,
            styleClass: 'error',
        };
    }

    getReceiptMethod(): Observable<Blob | null> {
        return this.getInternalReceipt();
    }

    getInternalReceipt(): Observable<Blob | null> {
        const transactionId = this.getTransactionId(this.successfulResponse);
        if (!transactionId) {
            return of(null);
        }

        const wasCartAboveThreshold = this.paymentConfirmationService.getWasCartAboveThreshold();

        if (this.configurationService.getEnableThresholdReceiptAPISwitch() && wasCartAboveThreshold) {
            const formDataArray = this.successfulResponse
                .map((resp) => {
                    if (resp.transactionId) {
                        return {
                            confirmationId: resp.transactionId,
                            invoices: this.getCompletedIds(resp.completedPayments || []),
                        };
                    }
                    return null;
                })
                .filter((x): x is { confirmationId: string; invoices: number[] } => x !== null);
            return this.paymentsService.getReceiptByPostAboveThreshold(formDataArray);
        }

        const legacyFormData = {
            confirmationId: transactionId,
            invoices: this.getCompletedIds(this.completedPayments),
        };
        return this.paymentsService.getReceiptByPost(legacyFormData);
    }

    getReceipt(): void {
        if (this.isDownloading) {
            return;
        }
        this.isDownloading = true;
        this.getReceiptMethod()
            .pipe(take(1))
            .subscribe({
                next: (receipt) => {
                    if (!receipt) {
                        return;
                    }
                    this.isDownloading = false;
                    const fileName = this.getFileName();
                    const file = new Blob([receipt], { type: receipt.type });

                    this.downloadFile(file, fileName);
                },
            });
    }

    private downloadFile(blob: Blob, fileName: string): void {
        /**
         * To know more about this any, please visit https://stackoverflow.com/questions/69552023/after-update-typescript-3-7-2-to-latest-typescript-4-4-4-error-ts2339
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const nav = window.navigator as any;

        // IE Browser
        if (nav && nav.msSaveOrOpenBlob) {
            nav.msSaveOrOpenBlob(blob, fileName);
            return;
        }

        // Other Browsers
        const pdfUrl = (window.URL || window.webkitURL).createObjectURL(blob);

        var PDF_link = document.createElement('a');
        PDF_link.href = pdfUrl;
        PDF_link.download = fileName;
        PDF_link.click();

        setTimeout(() => {
            window.URL.revokeObjectURL(pdfUrl);
        }, 1000);
    }

    backTo(): void {
        this.router.navigate(['./']);
    }

    getUniquePayments(completedPayments: CompletedPaymentOnPay[]): string[] {
        const uniquePayments: string[] = [];
        const displayedInvoices = new Set<string>();

        for (const payment of completedPayments) {
            if (payment.vendorInvoiceNumber) {
                if (!displayedInvoices.has(payment.vendorInvoiceNumber)) {
                    uniquePayments.push(payment.vendorInvoiceNumber);
                    displayedInvoices.add(payment.vendorInvoiceNumber);
                }
            } else {
                uniquePayments.push(payment.name);
            }
        }

        return uniquePayments;
    }

    subscribeConfirmationInformation(): void {
        this.paymentConfirmationService
            .getConfirmationInformation()
            .pipe(take(1))
            .subscribe({
                next: (information: PaymentResponse[] | null) => {
                    this.paymentResponse = information || [];
                    if (information?.length) {
                        this.processPaymentInformation(information);
                        this.uniquePayments = this.getUniquePayments(this.completedPayments);
                        this.records = this.getCompletedIds(this.completedPayments);
                        this.differentPaymentMethods = this.areDifferentPaymentMethods(this.successfulResponse);
                        this.isCreditPaymentMethod = this.getIsCreditPaymentMethod();
                        this.isTherePaymentMethodDifferentCredit = this.isTherePaymentMethodDifferentThanCredit();

                        if (this.isCreditPaymentMethod && this.isTherePaymentMethodDifferentCredit) {
                            this.paymentReceiptText = 'DOWNLOAD RECEIPT AND INVOICE(S)';
                        }
                        this.transactionsIds = this.getRequestTransactionsId(this.successfulResponse);
                    }
                },
            });
    }

    private processPaymentInformation(information: PaymentResponse[]): void {
        information.forEach((paymentElement: PaymentResponse) => {
            this.completedPayments.push(...paymentElement.completedPayments);
            this.totalPayments += paymentElement.totalPayments || 0;

            if (paymentElement.saleStatus === SaleStatus.SALE_SUCCESS) {
                this.notificationEmail = this.paymentConfirmationService.getEmailFromSuccessfulPayments();
                this.successfulResponse.push(paymentElement);
                this.countPaymentSuccessful += paymentElement.totalPayments || 0;
            } else {
                this.errorResponse.push(paymentElement);
                this.countPaymentErrors += paymentElement.totalPayments || 0;
            }
        });
    }

    getIsCreditPaymentMethod(): boolean {
        return this.successfulResponse?.some((paymentElement: PaymentResponse) => {
            return PaymentMethod.isCreditMethod(paymentElement.paymentMethod as PaymentMethodsType);
        });
    }

    isTherePaymentMethodDifferentThanCredit(): boolean {
        return this.successfulResponse?.some((paymentElement: PaymentResponse) => {
            return !PaymentMethod.isCreditMethod(paymentElement.paymentMethod as PaymentMethodsType);
        });
    }

    areDifferentPaymentMethods(responses: PaymentResponse[]): boolean {
        if (responses.length < 2) return false;

        const firstPaymentMethod = responses[0].paymentMethod;

        return responses.some((response) => response.paymentMethod !== firstPaymentMethod);
    }

    getTransactionId(payments: PaymentResponse[]): string {
        return payments.map((paymentElement: PaymentResponse) => paymentElement.transactionId)[0] || '';
    }

    getRequestTransactionsId(payments: PaymentResponse[]): string[] {
        return payments
            .map((paymentElement: PaymentResponse) => paymentElement.transactionId || '')
            .filter((id) => !!id);
    }

    getCompletedIds(completedPayments: CompletedPaymentOnPay[]): number[] {
        return completedPayments?.map((completed) => Number(completed.invoice?.id)).filter((id) => !!id) || [];
    }

    getFileName(): string {
        if (this.differentPaymentMethods) {
            if (this.isCreditPaymentMethod && this.isTherePaymentMethodDifferentCredit) {
                return 'Invoices and receipts ';
            }
            if (!this.isCreditPaymentMethod && this.isTherePaymentMethodDifferentCredit) {
                return 'Receipts';
            }
        }

        const transactionId = this.getTransactionId(this.successfulResponse);

        return 'Receipt-' + transactionId;
    }
    goToCart() {
        this.router.navigate(['/cart']);
    }
}
